import type { NaturalReceiverInteractionKind } from '@/adapters/primitive/_contract/natural/location';

export type NaturalColumnResult = {
    /** Unnormalised column value; its sign is what makes a crossing visible. */
    raw: number;
    digit: number;
    carryOut: number;
};

export function digitAtPower(args: { value: number; power: number }): number {
    return Math.floor(Math.abs(args.value) / 10 ** args.power) % 10;
}

export function powersForValue(value: number): number[] {
    const safeValue = Math.max(0, Math.floor(Math.abs(value)));
    if (safeValue === 0) return [0];
    const maxPower = Math.floor(Math.log10(safeValue));
    return Array.from({ length: maxPower + 1 }, (_, index) => index);
}

export function normalizeNaturalResultPowers(args: {
    powers: readonly number[];
    countedDigitByPower: Readonly<Partial<Record<number, number>>>;
}): { powers: number[]; removedPowers: number[] } {
    const highestCountedPower = Math.max(
        0,
        ...args.powers.filter((power) => (args.countedDigitByPower[power] ?? 0) > 0),
    );
    const powers = args.powers.filter((power) => power <= highestCountedPower);
    if (!powers.includes(0)) powers.unshift(0);

    return {
        powers,
        removedPowers: args.powers.filter((power) => power > highestCountedPower),
    };
}

export function resolveNaturalColumn(args: {
    radix: number;
    /** The band's value before this operand lands; reaches -1 when a deficit is pending. */
    valueBefore: number;
    operandDigit: number;
    sign: 1 | -1;
}): NaturalColumnResult {
    const { radix, valueBefore, operandDigit, sign } = args;
    const raw = valueBefore + sign * operandDigit;

    return {
        raw,
        digit: ((raw % radix) + radix) % radix,
        carryOut: Math.floor(raw / radix),
    };
}

export function toNaturalTargetSlot(args: { digit: number; slotOffset: number; radix: number }): number {
    const slot = (args.digit + args.slotOffset) % args.radix;
    return slot < 0 ? slot + args.radix : slot;
}

export function toNaturalTargetSlots(args: {
    digits: readonly number[];
    slotOffset: number;
    radix: number;
}): number[] {
    return args.digits
        .map((digit) => toNaturalTargetSlot({ digit, slotOffset: args.slotOffset, radix: args.radix }))
        .sort((left, right) => left - right);
}

export function toNaturalBandDigits(args: {
    slots: readonly number[];
    slotOffset: number;
    radix: number;
}): number[] {
    return args.slots
        .map((slot) => (slot - args.slotOffset + args.radix) % args.radix)
        .sort((left, right) => left - right);
}

export function toOperandSlotOffset(args: { valueBefore: number; raw: number }): number {
    return Math.min(args.valueBefore, args.raw);
}

export function toNaturalReceiverSlotOffset(args: {
    heldDigit: number;
    incomingDigit: number;
    interactionKind: NaturalReceiverInteractionKind;
}): number {
    return args.interactionKind === 'additive'
        ? args.heldDigit
        : args.heldDigit - args.incomingDigit;
}

export function toSlotRotation(args: { slotOffset: number; radix: number }): number {
    if (args.radix <= 0) return 0;
    return -(args.slotOffset / args.radix) * Math.PI * 2;
}

export function resolveOverhangDigits(args: {
    operandDigit: number;
    slotOffset: number;
    radix: number;
}): number[] {
    const { operandDigit, slotOffset, radix } = args;
    const overhangDigits: number[] = [];

    for (let digit = 1; digit <= operandDigit; digit += 1) {
        const landedSlot = digit + slotOffset;
        if (landedSlot <= 0 || landedSlot >= radix) overhangDigits.push(digit);
    }

    return overhangDigits;
}

export type NaturalCrossing = {
    digit: number;
    /** How far through the sweep this cell reaches position 0, as a fraction of the whole rotation. */
    fraction: number;
};

export function resolveCrossingSchedule(args: {
    operandDigit: number;
    slotOffset: number;
    radix: number;
    leadSlots?: number;
}): NaturalCrossing[] {
    const { operandDigit, slotOffset, radix } = args;
    if (operandDigit <= 0 || slotOffset === 0) return [];

    const sweepSlots = Math.abs(slotOffset);
    const leadSlots = args.leadSlots ?? 0;
    const crossings: NaturalCrossing[] = [];

    for (let digit = 1; digit <= operandDigit; digit += 1) {
        const slotsUntilCrossing = slotOffset > 0 ? radix - digit : digit;
        if (slotsUntilCrossing > sweepSlots) continue;
        crossings.push({
            digit,
            fraction: Math.min(1, Math.max(0, (slotsUntilCrossing - leadSlots) / sweepSlots)),
        });
    }

    return crossings.sort((left, right) => left.fraction - right.fraction);
}